import React, { useEffect, useState } from "react";
import {
  Card,
  Container,
  Row,
  CardBody,
  CardTitle,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Label,
  FormGroup,
} from "reactstrap";

import {
  getAllCategories,
  getCategoriesById,
  getAllPropertiesWithOutCat,
  assignPropertyToCategory,
  deletePropertyFromCategory
} from "service/api";
import Swal from "sweetalert2";

import DatatableTables_categories from "./DatatableTables_categories";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import AdminGuard from "helpers/AuthType/AdminGuard";

const AddCategories = () => {
  const [refreshTable, setRefreshTable] = useState(false);
  const [Property, setCategories] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [categoryDetails, setCategoryDetails] = useState([]);
  const [propertiesList, setPropertiesList] = useState([]);
  const [selectedPropertyId, setSelectedPropertyId] = useState("");

  useEffect(() => {
    getCategories();
  }, []);

  const getCategories = async () => {
    let response = await getAllCategories();
    setCategories(response.categories);
  };

  const fetchProperties = async (existingDetails) => {
    try {
      const res = await getAllPropertiesWithOutCat();
      if (res.success && res.properties) {
        const assignedIds = (existingDetails || []).map(d => d.property_id);
        const filtered = res.properties.filter(prop => !assignedIds.includes(prop.id));
        setPropertiesList(filtered);
      } else {
        setPropertiesList([]);
      }
    } catch (error) {
      console.error("Error fetching properties:", error);
      setPropertiesList([]);
    }
  };

  const handleViewRow = async (rowData) => {
    try {
      const res = await getCategoriesById(rowData.id);
      const details = res?.data?.data || [];
      setSelectedRowData(rowData);
      setCategoryDetails(details);
      await fetchProperties(details);
      setModalOpen(true);
    } catch (error) {
      console.error("Error fetching category details:", error);
    }
  };

  const handlePropertyChange = async (e) => {
    const propertyId = e.target.value;
    setSelectedPropertyId(propertyId);
    if (!propertyId) return;

    const loadingToast = toast.loading("Assigning property...");

    try {
      const payload = {
        category_id: selectedRowData.id,
        property_id: propertyId,
      };

      const res = await assignPropertyToCategory(payload);

      if (res.success) {
        toast.update(loadingToast, {
          render: res.message || "Deleted successfully!",
          type: "success",
          isLoading: false,
          autoClose: 3000,
          closeOnClick: true,
          pauseOnHover: true,
        });

        const updatedCategory = await getCategoriesById(selectedRowData.id);
        const details = updatedCategory?.data?.data || [];
        setCategoryDetails(details);
        await fetchProperties(details);
        setRefreshTable(prev => !prev);
      } else {
        toast.update(loadingToast, {
          render: res.message || "Failed to assign property.",
          type: "error",
          isLoading: false,
          autoClose: 3000,
          closeOnClick: true,
          pauseOnHover: true,
        });
      }
    } catch (error) {
      console.error("Error assigning property:", error);
      toast.update(loadingToast, {
        render: "An error occurred while assigning the property.",
        type: "error",
        isLoading: false,
        autoClose: 3000,
        closeOnClick: true,
        pauseOnHover: true,
      });
    }
  };
  const handleDeleteProperty = (propertyId) => {
    Swal.fire({
      title: "Are you sure?",
      text: "Do you want to remove this property from the category?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then(async (result) => {
      if (result.isConfirmed) {
        const loadingToast = toast.loading("Deleting property...");
        try {
          const res = await deletePropertyFromCategory({ property_id: propertyId });

          if (res?.data?.success) {
            const updatedCategory = await getCategoriesById(selectedRowData.id);
            const details = updatedCategory?.data?.data || [];
            setCategoryDetails(details);
            await fetchProperties(details);
            setRefreshTable(prev => !prev);

            toast.update(loadingToast, {
              render: res?.data?.message || "Property deleted successfully!",
              type: "success",
              isLoading: false,
              autoClose: 3000,
              closeOnClick: true,
              pauseOnHover: true,
            });
          } else {
            toast.update(loadingToast, {
              render: res?.data?.message || "Failed to delete property.",
              type: "error",
              isLoading: false,
              autoClose: 3000,
              closeOnClick: true,
              pauseOnHover: true,
            });
          }
        } catch (error) {
          console.error("Error deleting property:", error);
          toast.update(loadingToast, {
            render: "An error occurred while deleting the property.",
            type: "error",
            isLoading: false,
            autoClose: 3000,
            closeOnClick: true,
            pauseOnHover: true,
          });
        }
      }
    });
  };



  return (
    <AdminGuard>
      <React.Fragment>
        <div
          className="page-content"
          style={{
            color: "#fff",
            fontFamily: "'Roboto', sans-serif",
            minHeight: "100vh",
            background: "#0B132B",
            paddingTop: "30px",
            paddingBottom: "30px",
          }}
        >
          <Container fluid={true}>
            <Row>
              <Card>
                <CardBody>
                  <CardTitle className="mb-1">Categories</CardTitle>
                  <DatatableTables_categories
                    key={refreshTable}
                    onViewRow={handleViewRow}
                  />
                </CardBody>
              </Card>
            </Row>
          </Container>
        </div>

        <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)} size="lg">
          <ModalHeader toggle={() => setModalOpen(false)}>Category Details</ModalHeader>
          <ModalBody>
            {selectedRowData && (
              <>
                <div style={{ marginBottom: "1rem" }}>
                  <strong>Category:</strong> {selectedRowData.category_name}
                </div>

                <FormGroup>
                  <Label for="propertySelect">Select Property</Label>
                  <Input
                    type="select"
                    name="propertySelect"
                    id="propertySelect"
                    value={selectedPropertyId}
                    onChange={handlePropertyChange}
                  >
                    <option value="">-- Select Property --</option>
                    {propertiesList.map((property) => (
                      <option key={property.id} value={property.id}>
                        {property.title}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
              </>
            )}

            {categoryDetails.length > 0 ? (
              <table className="table table-bordered table-striped">
                <thead>
                  <tr>
                    <th>Sr. No.</th>
                    <th>Property Title</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryDetails.map((detail, index) => (
                    <tr key={detail.id}>
                      <td>{index + 1}</td>
                      <td>{detail.property_title}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-danger ml-2"
                          onClick={() => handleDeleteProperty(detail.property_id)}
                        >
                          <i className="mdi mdi-trash-can"></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <p>No details found for this category.</p>
            )}
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setModalOpen(false)}>
              Close
            </Button>
          </ModalFooter>
        </Modal>

        <ToastContainer />
      </React.Fragment>
    </AdminGuard>
  );
};

export default AddCategories;
